import React from 'react';
import { useNavigate } from 'react-router-dom';
import EmployerPageShell from '../../components/employer/EmployerPageShell';
import AiInterviewOrchestrationStatusPanel from './AiInterviewOrchestrationStatusPanel';
import AiInterviewOrchestrationSmokePanel from './AiInterviewOrchestrationSmokePanel';
import OwnedAvatarCapacityStatusPanel from './OwnedAvatarCapacityStatusPanel';

type WorkspaceStatus = 'Not started' | 'Incomplete' | 'In progress' | 'Ready to test' | 'Complete' | 'Blocked';

type WorkspaceItem = {
  title: string;
  status: WorkspaceStatus;
  routeOrComponent: string;
  note: string;
  nextAction: string;
  nextActionLabel?: string;
  details?: Array<{
    label: string;
    value: string;
  }>;
  progressPercent?: number;
  action?: {
    label: string;
    path: string;
  };
};

const statusClass: Record<WorkspaceStatus, string> = {
  'Not started': 'border-[#64748b] bg-[#f8fafc] text-[#475569]',
  Incomplete: 'border-[#d97706] bg-[#fffbeb] text-[#92400e]',
  'In progress': 'border-[#0284c7] bg-[#f0f9ff] text-[#075985]',
  'Ready to test': 'border-[#16a34a] bg-[#f0fdf4] text-[#166534]',
  Complete: 'border-[#0f766e] bg-[#ccfbf1] text-[#115e59]',
  Blocked: 'border-[#dc2626] bg-[#fef2f2] text-[#991b1b]',
};

const workspaceItems: WorkspaceItem[] = [
  {
    title: 'Job Setup',
    status: 'Complete',
    routeOrComponent: '/employer/create-job, /employer/jobs',
    note: 'Real Jason-authenticated backend smoke passed for create, list, reopen, edit, and save.',
    nextAction: 'Move to Candidate Applications.',
    action: { label: 'Open Jobs', path: '/employer/jobs' },
  },
  {
    title: 'Question Generation',
    status: 'Complete',
    routeOrComponent: 'JobCreate, JobEdit, POST /api/jobs/generate-questions',
    note: 'AI-first question flow is in place, manual fallback works, and all questions remain editable.',
    nextAction: 'Move to Candidate Applications.',
    action: { label: 'Create Job', path: '/employer/create-job' },
  },
  {
    title: 'Candidate Applications',
    status: 'Complete',
    routeOrComponent: '/applicant/jobs, /jobs/:jobId/apply',
    note: 'Real Jason-authenticated smoke passed for private application links, job-specific requirements, missing-document validation, real application submission, correct job association, and inline outcome display. Screening quality, document storage, and CV/payslip/transcript parsing quality are not complete yet.',
    nextAction: 'Move to Screening, Document Storage, and Parsing.',
    action: { label: 'View Candidate Jobs', path: '/applicant/jobs' },
  },
  {
    title: 'Screening',
    status: 'Complete',
    routeOrComponent: 'backend/src/services/screening',
    note: 'Deterministic screening rules, real Anthropic text-document provider smoke, and realistic rendered image/OCR provider smoke passed across anonymized CV, payslip, transcript, work-authorisation, qualification equivalence, location, experience, and tri-state decision evidence scenarios. Complete for the private screening scope, not a public launch.',
    nextAction: 'Move to Interview Recording.',
    progressPercent: 100,
  },
  {
    title: 'AI Model Architecture',
    status: 'In progress',
    routeOrComponent: 'config/ai-model-registry.json',
    note: 'The central model registry, Anthropic and OpenAI adapters, prompt versioning, usage and cost metering, and safe shadow and escalation controls are in place. Existing screening and interview behaviour remains protected behind conservative defaults.',
    nextAction: 'Complete the live document-pipeline comparison, apply the reviewed telemetry migration, prove deployed registry loading, and complete the Aura-2 avatar compatibility test.',
    progressPercent: 90,
  },
  {
    title: 'Interview Recording',
    status: 'Complete',
    routeOrComponent: '/employer/ai-interviews',
    note: 'The private Jason-only production-shaped orchestration smoke passed end to end through normal authentication: candidate camera/microphone, recording-before-dispatch, paid LiveAvatar LITE, the controlled two-answer interview, exactly one completion event, automatic Egress stop, automatic avatar/room shutdown and candidate disconnect, private MP4 audio/video playback, deletion with post-delete denial, and durable cleanup. A separate live run also proved complete backend shutdown/restart reconstruction and exact-session reconciliation. Complete for the intended private build scope; orchestration remains default off and this is not a public launch.',
    nextAction: 'Keep orchestration default off; create clean production resources and complete production launch monitoring before the first real user.',
    progressPercent: 100,
    action: { label: 'Open AI Interviews', path: '/employer/ai-interviews' },
  },
  {
    title: 'Owned Avatar',
    status: 'Complete',
    routeOrComponent: '/employer/ai-interviews/owned-avatar-demo',
    note: '100% technical build complete. Provider-aware capacity protection and monitoring are active. Jason’s startup, continuity-plan and final-experience acceptance remain pending; licensing is an external Jason-owned item.',
    details: [
      { label: 'Technical build', value: '100% Complete' },
      { label: 'Production voices', value: 'Clarkson / Linda / Pieter / Dana — Cartesia' },
      { label: 'Operational capacity', value: 'Admission protected; headroom above' },
      { label: 'Startup experience', value: 'Human acceptance pending' },
      { label: 'Continuity plan', value: 'Human approval pending' },
      { label: 'Final commercial acceptance', value: 'Pending' },
      { label: 'Licensing', value: 'External — owner: Jason' },
      { label: 'Latest milestone', value: 'Four Cartesia voices approved' },
    ],
    nextActionLabel: 'Exact next action',
    nextAction: 'Jason to review the startup experience, approve the continuity plan and complete final commercial acceptance.',
    progressPercent: 100,
    action: { label: 'Open owned-avatar demonstration', path: '/employer/ai-interviews/owned-avatar-demo' },
  },
  {
    title: 'Video Upload / Recording Test',
    status: 'Complete',
    routeOrComponent: '/video-test, VideoTest',
    note: 'Private recording, upload/storage, authenticated MP4 playback, and post-delete denial have been proven end to end for the intended AI interview flow. Complete for the private build scope, not a public launch.',
    nextAction: 'Use the retained test page for regression checks; move the build focus to Assessment / Employer Report.',
    progressPercent: 100,
    action: { label: 'Review Recording Test', path: '/video-test' },
  },
  {
    title: 'Transcription',
    status: 'Complete',
    routeOrComponent: 'Response.transcript',
    note: 'Transcription is in place for the intended private AI interview flow and is ready to support the next assessment and reporting workstream. Complete for the private build scope, not a public launch.',
    nextAction: 'Build Assessment / Employer Report next.',
    progressPercent: 100,
  },
  {
    title: 'Assessment / Employer Report',
    status: 'Not started',
    routeOrComponent: 'Assessment model',
    note: 'Assessment data structures exist, but there is no complete AI assessment or employer report workflow yet.',
    nextAction: 'Build the assessment output and employer report workflow next.',
  },
  {
    title: 'Candidate Report',
    status: 'Not started',
    routeOrComponent: 'CandidateReport model',
    note: 'The candidate report product is not built yet.',
    nextAction: 'Leave out of the flow until employer assessment reports are working.',
  },
  {
    title: 'Pricing & Billing',
    status: 'Not started',
    routeOrComponent: 'AI interview pricing and entitlements',
    note: 'Add complete interview pricing, corporate invoicing and controlled credit allocation for the 20-minute and 60-minute AI interview products. Role Simulation pricing remains separate.',
    nextAction: 'Implement pricing cards, package entitlements, pooled application-screen allowances, top-ups, opt-in overages, Xero invoice and EFT payment verification, PayFast self-service payment, and duplicate-safe credit allocation.',
    progressPercent: 0,
  },
  {
    title: 'Diagnostics / Build Status',
    status: 'In progress',
    routeOrComponent: 'This private workspace',
    note: 'This private workspace now tracks what is ready for testing and what still needs build work.',
    nextAction: 'Keep updating statuses as each private AI interview section is tested.',
  },
  {
    title: 'Long-duration Interview Launch Test',
    status: 'Complete',
    routeOrComponent: 'Private 20-minute and 60-minute launch validation',
    note: 'Both genuine final-system duration gates passed. The 20-minute interview completed 12 ordered fixed questions and 2 contextual follow-ups; the 59m 38.545s interview completed 34 ordered fixed questions and 7 contextual follow-ups. Both retained private recording, complete question-linked transcript evidence, authenticated playback and clean completion/cleanup.',
    nextAction: 'Retain the duration evidence; no further long-duration run is required for this workstream.',
    progressPercent: 100,
  },
];

const statusLegend: Array<{ status: WorkspaceStatus; description: string }> = [
  { status: 'Ready to test', description: 'Private Jason smoke test is the next step.' },
  { status: 'In progress', description: 'Being actively shaped or tracked.' },
  { status: 'Incomplete', description: 'Exists, but is not ready for end-to-end use.' },
  { status: 'Not started', description: 'No complete workflow exists yet.' },
  { status: 'Blocked', description: 'Needs a prerequisite before progress can continue.' },
  { status: 'Complete', description: 'Validated for the intended private scope.' },
];

const AiInterviewsWorkspace: React.FC = () => {
  const navigate = useNavigate();

  return (
    <EmployerPageShell activeSection="ai-interviews">
      <section className="flex flex-col gap-4 rounded-[8px] border border-[#dbe7e5] bg-white px-5 py-5 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0d9488]">Private Jason workspace</p>
            <h1 className="mt-2 font-display text-[26px] font-bold text-[#0f172a]">AI Interviews</h1>
            <p className="mt-1 max-w-[760px] text-[13px] leading-6 text-[#64748b]">
              A private map of the dormant AI interview product. This area is locked down while the live product remains focused on role simulations.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate('/employer/dashboard')}
              className="rounded-[8px] border border-[#0d9488] bg-white px-3 py-2 text-[12px] font-semibold text-[#0d9488] hover:bg-[#f0fdfa]"
            >
              Role Simulations
            </button>
            <button
              type="button"
              className="rounded-[8px] border border-[#0d9488] bg-[#0d9488] px-3 py-2 text-[12px] font-semibold text-white"
            >
              AI Interviews
            </button>
          </div>
        </div>
      </section>

      <section
        aria-label="Owned Avatar current position"
        className="rounded-[8px] border border-[#5eead4] bg-[#f0fdfa] px-5 py-4 shadow-sm"
      >
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0f766e]">
          Current position
        </p>
        <h2 className="mt-1 font-display text-[20px] font-bold text-[#0f172a]">
          Owned Avatar — 100% Technical Build Complete
        </h2>
        <p className="mt-2 text-[13px] leading-6 text-[#475569]">
          Technical engineering is banked. Jason’s startup, continuity-plan and final-experience acceptance remain separate launch gates; commercial licensing remains an external Jason-owned item.
        </p>
      </section>

      <OwnedAvatarCapacityStatusPanel />

      <AiInterviewOrchestrationStatusPanel />
      <AiInterviewOrchestrationSmokePanel />

      <section className="rounded-[8px] border border-[#dbe7e5] bg-white px-5 py-4 shadow-sm">
        <h2 className="text-[14px] font-bold text-[#0f172a]">Build status legend</h2>
        <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {statusLegend.map((item) => (
            <div key={item.status} className="flex items-center gap-2 text-[12px] text-[#64748b]">
              <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold ${statusClass[item.status]}`}>
                {item.status}
              </span>
              <span>{item.description}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {workspaceItems.map((item) => {
          return <article
            key={item.title}
            aria-label={item.title}
            className="flex min-h-[270px] flex-col rounded-[8px] border border-[#e2e8f0] bg-white p-5 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-[16px] font-bold text-[#0f172a]">{item.title}</h2>
              <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold ${statusClass[item.status]}`}>
                {item.status}
              </span>
            </div>
            <div className="mt-4 rounded-[6px] bg-[#f8fafc] px-3 py-2 text-[12px] font-semibold text-[#475569]">
              {item.routeOrComponent}
            </div>
            <p className="mt-4 text-[13px] leading-6 text-[#64748b]">{item.note}</p>
            {item.details && (
              <dl className="mt-4 grid gap-2 rounded-[6px] border border-[#e2e8f0] bg-[#f8fafc] px-3 py-3 text-[12px]">
                {item.details.map((detail) => (
                  <div key={detail.label} className="grid gap-1 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] sm:gap-3">
                    <dt className="font-semibold text-[#64748b]">{detail.label}</dt>
                    <dd className="min-w-0 break-words font-bold text-[#334155] sm:text-right">{detail.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {typeof item.progressPercent === 'number' && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-[12px] font-semibold text-[#475569]">
                  <span>Progress</span>
                  <span>{item.progressPercent}%</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-[#e2e8f0]">
                  <div
                    className="h-2 rounded-full bg-[#0d9488]"
                    style={{ width: `${item.progressPercent}%` }}
                    aria-label={`${item.title} progress ${item.progressPercent}%`}
                  />
                </div>
              </div>
            )}
            <div className="mt-4 flex-1 rounded-[6px] border border-[#e2e8f0] bg-[#ffffff] px-3 py-2">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#94a3b8]">
                {item.nextActionLabel || 'Next action'}
              </p>
              <p className="mt-1 text-[13px] leading-5 text-[#475569]">{item.nextAction}</p>
            </div>
            {item.action ? (
              <button
                type="button"
                onClick={() => navigate(item.action!.path)}
                className="mt-5 self-start rounded-[8px] border border-[#0d9488] bg-[#0d9488] px-3 py-2 text-[12px] font-semibold text-white hover:bg-[#0f766e]"
              >
                {item.action.label}
              </button>
            ) : (
              <div className="mt-5 text-[12px] font-semibold text-[#94a3b8]">No safe page to open yet</div>
            )}
          </article>;
        })}
      </section>
    </EmployerPageShell>
  );
};

export default AiInterviewsWorkspace;
